import { useState } from 'react';
import { useTranslation } from '@doccareplus/i18n';
import { detectDefaultCountry, dialCodeFor, isValidNationalPhoneNumber, toE164 } from '@doccareplus/shared/lib/phone';
import { selectActiveContext, useAppSelector } from '@doccareplus/kernel';
import { useQuickPatientDuplicateCheckCommand, useQuickRegisterPatientCommand } from '../application/scheduling.command';
import type { PatientPickOption, QuickPatientDuplicateMatch } from '../infrastructure/scheduling.api';

export interface QuickRegisterFields {
  fullName: string;
  phoneCountry: string;
  phone: string;
  dateOfBirth: string;
  gender: string;
  bloodGroup: string;
  email: string;
  address: string;
  notes: string;
}

export interface QuickRegisterPrefill {
  name?: string;
  phone?: string;
  phoneCountry?: string;
}

function emptyFields(defaultCountry: string, prefill?: QuickRegisterPrefill): QuickRegisterFields {
  return {
    fullName: prefill?.name ?? '',
    phoneCountry: prefill?.phoneCountry || defaultCountry,
    phone: prefill?.phone ?? '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    email: '',
    address: '',
    notes: '',
  };
}

export interface UseQuickRegisterPatientStep {
  fields: QuickRegisterFields;
  setField: (name: keyof QuickRegisterFields, value: string) => void;
  isSubmitting: boolean;
  error: string | null;
  duplicateMatches: QuickPatientDuplicateMatch[];
  duplicateReason: string;
  setDuplicateReason: (value: string) => void;
  /** Validates, runs the duplicate check once, then registers. Resolves the created patient, else null. */
  submit: () => Promise<PatientPickOption | null>;
  /** Re-initializes all fields — the caller re-enters this step on the render-phase reset pattern. */
  resetFields: (prefill?: QuickRegisterPrefill) => void;
}

/**
 * useQuickRegisterPatientStep — Step 1 of the booking "register patient" stepper: full patient fields
 * (name + phone required, the rest optional), the same duplicate-check + override-reason flow as the
 * patients-feature register form. Registration commits immediately on submit — the patient exists in
 * the clinic directory whether or not the caller goes on to complete Step 2 (booking details).
 */
export function useQuickRegisterPatientStep(prefill?: QuickRegisterPrefill): UseQuickRegisterPatientStep {
  const { t } = useTranslation('appointments');
  const register = useQuickRegisterPatientCommand();
  const duplicateCheck = useQuickPatientDuplicateCheckCommand();
  const clinicCountry = useAppSelector(selectActiveContext)?.country;

  const [fields, setFields] = useState<QuickRegisterFields>(() => emptyFields(clinicCountry || detectDefaultCountry(), prefill));
  const [error, setError] = useState<string | null>(null);
  const [duplicateMatches, setDuplicateMatches] = useState<QuickPatientDuplicateMatch[]>([]);
  const [duplicateReason, setDuplicateReason] = useState('');
  const [reviewedFingerprint, setReviewedFingerprint] = useState<string | null>(null);

  const resetFields = (nextPrefill?: QuickRegisterPrefill) => {
    setFields(emptyFields(clinicCountry || detectDefaultCountry(), nextPrefill));
    setError(null);
    setDuplicateMatches([]);
    setDuplicateReason('');
    setReviewedFingerprint(null);
  };

  const setField = (name: keyof QuickRegisterFields, value: string) => {
    setFields((current) => ({ ...current, [name]: value }));
    if (name === 'fullName' || name === 'phoneCountry' || name === 'phone') {
      setDuplicateMatches([]);
      setDuplicateReason('');
      setReviewedFingerprint(null);
      setError(null);
    }
  };

  const submit = async (): Promise<PatientPickOption | null> => {
    setError(null);
    const fullName = fields.fullName.trim();
    if (!fullName) {
      setError(t('booking.quickRegister.errors.nameRequired', { defaultValue: 'Full name is required' }));
      return null;
    }
    if (!fields.phone.trim() || !isValidNationalPhoneNumber(fields.phoneCountry, fields.phone)) {
      setError(fields.phoneCountry === 'IN' ? t('booking.quickRegister.phoneIndiaLength') : t('booking.quickRegister.phoneInvalid'));
      return null;
    }
    const phone = toE164(dialCodeFor(fields.phoneCountry) ?? '', fields.phone);

    const fingerprint = `${fullName.toLowerCase()}|${phone}`;
    if (reviewedFingerprint !== fingerprint) {
      const matches = await duplicateCheck.mutateAsync({ fullName, phone });
      setReviewedFingerprint(fingerprint);
      setDuplicateMatches(matches);
      if (matches.length > 0) return null;
    }

    if (duplicateMatches.length > 0 && duplicateReason.trim().length < 5) {
      setError(t('booking.quickRegister.reasonRequired'));
      return null;
    }

    try {
      return await register.mutateAsync({
        fullName,
        phone,
        dateOfBirth: fields.dateOfBirth || undefined,
        gender: fields.gender || undefined,
        bloodGroup: fields.bloodGroup || undefined,
        email: fields.email.trim() || undefined,
        address: fields.address.trim() || undefined,
        notes: fields.notes.trim() || undefined,
        duplicateOverrideReason: duplicateMatches.length ? duplicateReason.trim() : undefined,
      });
    } catch {
      setError(t('common:errors.generic', { defaultValue: 'Something went wrong. Please try again.' }));
      return null;
    }
  };

  return {
    fields,
    setField,
    isSubmitting: register.isPending || duplicateCheck.isPending,
    error,
    duplicateMatches,
    duplicateReason,
    setDuplicateReason,
    submit,
    resetFields,
  };
}
